"use client";

import * as React from "react";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Receipt,
  XCircle,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { faDateTime, faToman, formatCard, toFa } from "@/lib/format";

import { UploadReceiptDialog } from "./UploadReceiptDialog";

export type Plan = {
  id: string;
  name: string;
  priceToman: number;
  durationDays: number;
  trafficGB: number;
};

export type Payment = {
  id: string;
  amountToman: number;
  senderCard: string;
  receiptImage: string | null;
  trackingCode: string | null;
  note: string | null;
  status: "PENDING" | "APPROVED" | "REJECTED";
  adminNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
  plan: Plan;
};

function statusBadge(status: Payment["status"]) {
  if (status === "APPROVED") {
    return (
      <Badge className="gap-1 border-emerald-500/30 bg-emerald-500/15 text-emerald-500">
        <CheckCircle2 className="size-3" /> تأیید شده
      </Badge>
    );
  }
  if (status === "REJECTED") {
    return (
      <Badge
        variant="destructive"
        className="gap-1 border-red-500/30 bg-red-500/15 text-red-500"
      >
        <XCircle className="size-3" /> رد شده
      </Badge>
    );
  }
  return (
    <Badge className="gap-1 border-amber-500/30 bg-amber-500/15 text-amber-500">
      <Clock className="size-3" /> در انتظار تأیید
    </Badge>
  );
}

export function PaymentHistory({ payments }: { payments: Payment[] }) {
  const [uploadingId, setUploadingId] = React.useState<string | null>(null);

  if (payments.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed bg-card p-8 text-center">
        <Receipt className="mx-auto size-8 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">
          هنوز پرداختی ثبت نکرده‌اید.
        </p>
      </div>
    );
  }

  const activePayment = payments.find((p) => p.id === uploadingId) || null;

  return (
    <>
      <div className="overflow-hidden rounded-2xl border bg-card">
        <ul className="scrollbar-thin max-h-96 divide-y overflow-y-auto">
          {payments.map((p) => (
            <li
              key={p.id}
              className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{p.plan.name}</span>
                  {statusBadge(p.status)}
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                  <span>{faDateTime(p.createdAt)}</span>
                  <span dir="ltr">{formatCard(p.senderCard)}</span>
                  {p.trackingCode && (
                    <span>
                      کد رهگیری: <span dir="ltr">{toFa(p.trackingCode)}</span>
                    </span>
                  )}
                </div>
                {p.status === "REJECTED" && p.adminNote && (
                  <p className="flex items-start gap-1 text-xs text-red-500">
                    <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
                    {p.adminNote}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="text-left">
                  <p className="text-sm font-bold">{faToman(p.amountToman)}</p>
                </div>
                {p.status === "PENDING" && !p.receiptImage && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    onClick={() => setUploadingId(p.id)}
                  >
                    <Receipt className="size-3.5" /> آپلود فیش
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {activePayment && (
        <UploadReceiptDialog
          payment={activePayment}
          onClose={() => setUploadingId(null)}
          onUploaded={() => setUploadingId(null)}
        />
      )}
    </>
  );
}

export default PaymentHistory;
